/**
 * Active Document Tracker
 * 
 * This module tracks which documents are currently being edited
 * and provides real-time updates to the SchedulesTable component.
 */

import { getSupabase } from '../components/supabaseClient';
import globalPresenceManager from './globalPresenceManager';
import { dbgLog } from './appDebugFlags';

const adLog = (...args) => dbgLog('active-doc-tracker', ...args);

class ActiveDocumentTracker {
  constructor() {
    this.activeDocuments = new Map(); // documentPath -> { users, lastActivity }
    this.subscribers = new Set();
    this.supabase = null;
    this.presenceChannel = null;
    this.presenceUpdateTimeout = null; // Debounce presence updates
  }

  /**
   * Initialize the active document tracker
   */
  async initialize() {
    if (this.presenceChannel) {
      adLog('📄 Active document tracker: Already initialized, skipping');
      return;
    }
    
    this.supabase = await getSupabase();
    await this.setupPresenceChannel();
  }

  /**
   * Set up presence channel to track active documents
   */
  async setupPresenceChannel() {
    if (!this.supabase) {
      console.error('❌ Active document tracker: No Supabase client available');
      return;
    }

    adLog('🔌 Active document tracker: Setting up presence channel...');

    try {
      this.presenceChannel = this.supabase.channel('active_documents', {
        config: {
          presence: {
            key: 'active_documents'
          }
        }
      });

      adLog('🔌 Active document tracker: Presence channel created');

      // Listen for presence changes
      this.presenceChannel
        .on('presence', { event: 'sync' }, () => {
          adLog('🔌 Active document tracker: Presence sync event received');
          this.handlePresenceSync();
        })
        .on('presence', { event: 'join' }, ({ newPresences }) => {
          adLog('🔌 Active document tracker: Document join event received:', newPresences);
          this.handleDocumentJoin(newPresences);
        })
        .on('presence', { event: 'leave' }, ({ leftPresences }) => {
          adLog('🔌 Active document tracker: Document leave event received:', leftPresences);
          this.handleDocumentLeave(leftPresences);
        });

      adLog('🔌 Active document tracker: Subscribing to presence channel...');
      const subscribeResult = await this.presenceChannel.subscribe();
      adLog('✅ Active document tracker initialized');
      adLog('📄 Active document tracker: Presence channel subscription result:', subscribeResult);
      
      if (subscribeResult === 'SUBSCRIBED') {
        adLog('✅ Active document tracker: Successfully subscribed to presence channel');
      } else {
        console.warn('⚠️ Active document tracker: Unexpected subscription result:', subscribeResult);
      }
    } catch (error) {
      console.error('❌ Error setting up active document tracker:', error);
      console.error('❌ Error details:', error.message, error.code);
    }
  }

  /**
   * Track a document as active
   * @param {string} documentPath - Path to the document
   * @param {string} patientId - Patient ID
   * @param {string} patientName - Patient name
   * @param {string} userId - User ID
   * @param {string} userName - User name
   */
  async trackDocument(documentPath, patientId, patientName, userId, userName) {
    if (!this.presenceChannel) {
      adLog('❌ Active document tracker: No presence channel available');
      return;
    }
    
    adLog('📄 Active document tracker: Presence channel state:', this.presenceChannel.state);

    try {
      adLog('📄 Active document tracker: Tracking document:', {
        documentPath,
        patientId,
        patientName,
        userId,
        userName
      });
      
      await this.presenceChannel.track({
        document_path: documentPath,
        patient_id: patientId,
        patient_name: patientName,
        user_id: userId,
        user_name: userName,
        last_activity: new Date().toISOString(),
        is_active: true
      });
      
      adLog('✅ Active document tracker: Document tracking successful');
    } catch (error) {
      console.error('❌ Error tracking document:', error);
    }
  }

  /**
   * Stop tracking a document
   * @param {string} documentPath - Path to the document
   */
  async untrackDocument(documentPath) {
    if (!this.presenceChannel) return;

    try {
      // Remove from presence tracking
      await this.presenceChannel.untrack();
    } catch (error) {
      console.error('❌ Error untracking document:', error);
    }
  }

  /**
   * Handle presence sync events
   */
  handlePresenceSync() {
    if (!this.presenceChannel) return;

    // Debounce presence updates to avoid spam
    if (this.presenceUpdateTimeout) {
      clearTimeout(this.presenceUpdateTimeout);
    }

    this.presenceUpdateTimeout = setTimeout(() => {
      const presenceState = this.presenceChannel.presenceState();
      const allDocuments = Object.values(presenceState).flat();
      
      // Update active documents map
      this.activeDocuments.clear();
      
      allDocuments.forEach(doc => {
        if (doc.is_active && doc.document_path) {
          const key = doc.document_path;
          if (!this.activeDocuments.has(key)) {
            this.activeDocuments.set(key, {
              users: [],
              lastActivity: doc.last_activity,
              patientId: doc.patient_id,
              patientName: doc.patient_name
            });
          }
          
          const docInfo = this.activeDocuments.get(key);
          docInfo.users.push({
            id: doc.user_id,
            name: doc.user_name,
            lastActivity: doc.last_activity
          });
        }
      });

      // Notify subscribers
      this.notifySubscribers();
    }, 200); // 200ms debounce
  }

  /**
   * Handle document join events
   * @param {Array} newPresences - New document presences
   */
  handleDocumentJoin(newPresences) {
    adLog('📄 Document joined:');
    newPresences.forEach((presence, index) => {
      adLog(`  [${index}] Document path: ${presence.document_path}`);
      adLog(`  [${index}] Patient ID: ${presence.patient_id}`);
      adLog(`  [${index}] Patient name: ${presence.patient_name}`);
      adLog(`  [${index}] User ID: ${presence.user_id}`);
      adLog(`  [${index}] User name: ${presence.user_name}`);
      adLog(`  [${index}] Last activity: ${presence.last_activity}`);
      adLog(`  [${index}] Is active: ${presence.is_active}`);
    });
    this.handlePresenceSync();
  }

  /**
   * Handle document leave events
   * @param {Array} leftPresences - Documents that were left
   */
  handleDocumentLeave(leftPresences) {
    adLog('📄 Document left:');
    const currentUser = globalPresenceManager.getCurrentUser?.();
    leftPresences.forEach((presence, index) => {
      adLog(`  [${index}] Document path: ${presence.document_path}`);
      adLog(`  [${index}] Patient ID: ${presence.patient_id}`);
      adLog(`  [${index}] Patient name: ${presence.patient_name}`);
      adLog(`  [${index}] User ID: ${presence.user_id}`);
      adLog(`  [${index}] User name: ${presence.user_name}`);
      adLog(`  [${index}] Last activity: ${presence.last_activity}`);
      adLog(`  [${index}] Is active: ${presence.is_active}`);
      // Start grace period so avatar goes stale for users who left (e.g. navigated to Schedules)
      if (presence.user_id && (!currentUser || presence.user_id !== currentUser.id)) {
        try {
          const existingUser = globalPresenceManager.activeUsers.get(presence.user_id);
          const cachedColor = globalPresenceManager.getCachedUserColor?.(presence.user_id);
          const preservedColor = cachedColor || existingUser?.color;
          const userData = {
            user_id: presence.user_id,
            name: presence.user_name || 'Unknown',
            document_path: presence.document_path,
            patient_id: presence.patient_id,
            patient_name: presence.patient_name,
            last_activity: presence.last_activity || new Date().toISOString(),
            source: 'active-document-leave',
            ...(preservedColor && { color: preservedColor })
          };
          globalPresenceManager.startGracePeriod(presence.user_id, userData);
        } catch (e) {
          console.warn('Active document tracker: failed to start grace period for left user', e);
        }
      }
    });
    this.handlePresenceSync();
  }

  /**
   * Subscribe to active document updates
   * @param {Function} callback - Callback function to receive updates
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify all subscribers of updates
   */
  notifySubscribers() {
    const activeDocs = Array.from(this.activeDocuments.entries()).map(([path, info]) => ({
      path,
      patientId: info.patientId,
      patientName: info.patientName,
      users: info.users,
      lastActivity: info.lastActivity
    }));

    this.subscribers.forEach(callback => {
      try {
        callback(activeDocs);
      } catch (error) {
        console.error('❌ Error in subscriber callback:', error);
      }
    });
  }

  /**
   * Get currently active documents
   * @returns {Array} Array of active document info
   */
  getActiveDocuments() {
    return Array.from(this.activeDocuments.entries()).map(([path, info]) => ({
      path,
      patientId: info.patientId,
      patientName: info.patientName,
      users: info.users,
      lastActivity: info.lastActivity
    }));
  }

  /**
   * Check if a patient has an active document
   * @param {string} patientId - Patient ID to check
   * @returns {boolean} Whether the patient has an active document
   */
  isPatientActive(patientId) {
    return Array.from(this.activeDocuments.values()).some(doc => 
      doc.patientId === patientId
    );
  }

  /**
   * Get active users for a specific patient
   * @param {string} patientId - Patient ID
   * @returns {Array} Array of active users
   */
  getActiveUsersForPatient(patientId) {
    const activeDoc = Array.from(this.activeDocuments.values()).find(doc => 
      doc.patientId === patientId
    );
    return activeDoc ? activeDoc.users : [];
  }

  /**
   * Clean up the tracker
   */
  cleanup() {
    if (this.presenceUpdateTimeout) {
      clearTimeout(this.presenceUpdateTimeout);
    }

    if (this.presenceChannel) {
      this.presenceChannel.unsubscribe();
      this.presenceChannel = null;
    }
    
    this.activeDocuments.clear();
    this.subscribers.clear();
  }
}

// Create singleton instance
const activeDocumentTracker = new ActiveDocumentTracker();

export default activeDocumentTracker;
